<script setup>
import mushroom from "cem-probe-api";
import { computed, defineProps, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { selectVlan } from "@/stores/changeSelectVlan";
import { datePicker } from "@/stores/datePicker";
import { langWeb } from "@/stores/lang";
import MethodService from "@/service/MethodService";
import { getSystemType } from "@/utils/systemType";
const storeDatePicker = datePicker();
const storeSelectVlan = selectVlan();
const lang_web = langWeb();
const route = useRoute();
const router = useRouter();
const props = defineProps({
  items: Array,
});
const nameService = ref("");
const nameProbe = ref("");
const summaryName = ref("");
const isSummary = computed(() => getSystemType() === "summary");

// Gọi API lấy tên probe từ pid_probe
const fetchProbeName = async () => {
  const pidProbe = localStorage.getItem("pid_probe") || "";
  if (!pidProbe) {
    nameProbe.value = "CEM";
    return;
  }
  try {
    const res = await mushroom.probe.getProbeInformationAsync({
      body: {
        probe_id: pidProbe
      }
    });
    nameProbe.value = res?.result?.name || "CEM";
  } catch (e) {
    console.error("Lỗi lấy tên probe: %o", e);
    nameProbe.value = "CEM";
  }
};

// Gọi API lấy summaryName từ dynamic_dashboard → menu dựa vào id trên URL
const fetchSummaryName = async () => {
  const dashboardId = route.params?.id;
  if (!dashboardId) {
    summaryName.value = "";
    return;
  }
  try {
    const dashboardRes = await mushroom.dynamic_dashboard.findByIdAsync({
      id: dashboardId,
    });
    const menuId = dashboardRes.result?.menu_id;
    if (!menuId) {
      summaryName.value = "";
      return;
    }
    const menuRes = await mushroom.menu.findByIdAsync({
      id: menuId,
    });
    if (menuRes.result) {
      const currentLang = lang_web.showLang; // "vi" hoặc "en"
      const labelLang = menuRes.result.label_lang?.[currentLang];
      // Ưu tiên label_lang theo ngôn ngữ hiện tại, nếu không có mới lấy label
      summaryName.value =
        labelLang && labelLang.trim() !== ""
          ? labelLang
          : menuRes.result.label || "";
    } else {
      summaryName.value = "";
    }
  } catch (e) {
    console.error("Lỗi lấy summaryName từ API: %o", e);
    summaryName.value = "";
  }
};

const handleBreadcrumbClick = (event, item, index) => {
  if (index === 0 && isSummary.value) {
    const urlPrimary = localStorage.getItem("urlPrimary");
    if (urlPrimary) {
      event.preventDefault();
      window.location.href = urlPrimary;
    }
  }
};

// Thay thế probeName trong breadcrumb item đầu tiên (href="/home") bằng giá trị từ ref
const breadcrumbItems = computed(() => {
  if (!props.items) return [];
  const defaultHomeText = lang_web.showLang === "vi" ? "Trang chủ" : "Home";
  return props.items.map((item, index) => {
    if (index === 0 && item.href === "/home") {
      let displayText = nameProbe.value;
      if (isSummary.value) {
        if (summaryName.value) {
          displayText = summaryName.value;
        } else {
          displayText = defaultHomeText;
        }
      }
      return { ...item, text: displayText || defaultHomeText };
    }
    return item;
  });
});
const nameVlan = ref("VLAN : ALL");
const urlVlan = ref("");
const nameUsecases = ref("");
const nameMenuParent = ref(""); // menu cha dynamic
const nameMenuChild = ref(""); // menu con dynamic
const nameTableScreen = ref("");
const nameHealthGroup = ref("");

const getNameService = async () => {
  try {
    const response = await mushroom.service.findByIdAsync(
      {
        id: route.params?.id,
      },
      { enabledCache: false },
    );
    nameService.value = response.result.name;
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};
const getNameDynamic = async () => {
  try {
    const response = await mushroom.dynamic_dashboard.findByIdAsync({
      id: route.params?.id,
    });
    if (response.result?.menu_id) {
      const res = await mushroom.menu.findByIdAsync({
        id: response.result?.menu_id,
      });
      const currentLang = lang_web.showLang;
      const getLabel = (menuItem) => {
        const labelLang = menuItem?.label_lang?.[currentLang];
        return labelLang && labelLang.trim() !== ""
          ? labelLang
          : menuItem?.label || "";
      };
      if (!res.result?.parent_id) {
        nameMenuParent.value = getLabel(res.result);
      } else {
        nameMenuChild.value = getLabel(res.result);
        const resMenuParent = await mushroom.menu.findByIdAsync({
          id: res.result.parent_id,
        });
        if (resMenuParent.result)
          nameMenuParent.value = getLabel(resMenuParent.result);
      }
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};
const getNameUsecases = async () => {
  if (route.query.ip) {
    nameUsecases.value = "- " + route.query.ip;
  }
};
const trimVlan = (str) => {
  if (!str) return;
  return storeSelectVlan.getValueVlan.substr(
    storeSelectVlan.getValueVlan.lastIndexOf(":") + 1,
  );
};
const hashUrl = () => {
  if (storeSelectVlan.getValueVlan === "all") {
    router.push({
      path: "/service-health",
      query: {
        start_date: MethodService.formatDateYYYYMMDD(
          storeDatePicker.showDatePicker[0],
        ),
        stop_date: MethodService.formatDateYYYYMMDD(
          storeDatePicker.showDatePicker[1],
        ),
      },
    });
  } else {
    router.push({
      path: "/service-health",
      query: {
        start_date: MethodService.formatDateYYYYMMDD(
          storeDatePicker.showDatePicker[0],
        ),
        stop_date: MethodService.formatDateYYYYMMDD(
          storeDatePicker.showDatePicker[1],
        ),
        vlan: trimVlan(storeSelectVlan.getValueVlan), // xem lại giá trị vlan từ store
      },
    });
  }
};

const getNameTableScreen = async () => {
  try {
    const response = await mushroom.table_screen.findByIdAsync(
      {
        id: route.params?.id,
      },
      { enabledCache: false },
    );
    nameTableScreen.value = response.result.name;
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};
const getNameHealthGroup = async () => {
  if (
    route.name === "ServiceHealth" &&
    route.params?.id &&
    route.query?.type === "group"
  ) {
    try {
      const response = await mushroom.service_health_group.findByIdAsync({
        id: route.params.id,
      });
      if (response.result) {
        nameHealthGroup.value = response.result.name;
      }
    } catch (e) {
      console.error("Có lỗi: %o", e);
    }
  } else {
    nameHealthGroup.value = "";
  }
};

watch(
  () => storeSelectVlan.getValueVlan,
  (valueVlan) => {
    valueVlan.indexOf("VLAN") !== -1
      ? (nameVlan.value = valueVlan)
      : (nameVlan.value =
          "VLAN : " + (valueVlan === "all" ? "ALL" : valueVlan));
    urlVlan.value = window.location.search;
  },
);
watch(
  () => route.params?.id,
  async () => {
    if (route.name === "DichVu") await getNameService();
    if (route.name === "Usecases621") await getNameUsecases();
    if (route.name === "Dynamic") await getNameDynamic();
    if (route.name === "TableScreen") await getNameTableScreen();
    if (route.name === "ServiceHealth") await getNameHealthGroup();
    if (isSummary.value) await fetchSummaryName();
  },
);
watch(
  () => route.query?.type,
  async () => {
    if (route.name === "ServiceHealth") await getNameHealthGroup();
  },
);

onMounted(async () => {
  // Gọi API lấy tên probe cho breadcrumb (chỉ probe, không phải summary)
  if (!isSummary.value) {
    await fetchProbeName();
  }
  if (isSummary.value) await fetchSummaryName();
  if (route.name === "DichVu") await getNameService();
  if (route.name === "Usecases621") await getNameUsecases();
  if (route.name === "Dynamic") await getNameDynamic();
  if (route.name === "TableScreen") await getNameTableScreen();
  if (route.name === "ServiceHealth") await getNameHealthGroup();
});
</script>

<template>
  <!-- start page title -->
  <div class="row d-none d-lg-block">
    <div class="col-12">
      <div
        class="page-title-box d-sm-flex align-items-center justify-content-between"
      >
        <div class="page-title-right">
          <ol class="breadcrumb m-0">
            <!-- ServiceHealth breadcrumb -->
            <template v-if="route.name === 'ServiceHealth'">
              <template v-for="(item, index) in breadcrumbItems" :key="index">
                <li class="breadcrumb-item" :class="{ active: item[0] }">
                  <router-link
                    :to="{ path: `${item.href}` }"
                    class="breadcrumb-link"
                    >{{
                      index === 1 && nameHealthGroup
                        ? nameHealthGroup
                        : item.text
                    }}</router-link
                  >
                </li>
                <li
                  class="breadcrumb-item"
                  v-if="index === 0 && !isSummary"
                >
                  <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                    nameProbe
                  }}</router-link>
                </li>
              </template>
              <li class="breadcrumb-item" @click="hashUrl">
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameVlan
                }}</router-link>
              </li>
            </template>

            <!-- Summary / Dynamic breadcrumb -->
            <template v-else-if="isSummary">
              <template v-for="(item, index) in breadcrumbItems" :key="index">
                <li class="breadcrumb-item" :class="{ active: item[0] }">
                  <router-link
                    :to="{ path: `${item.href}` }"
                    class="breadcrumb-link"
                    @click="handleBreadcrumbClick($event, item, index)"
                    >{{ item.text }}</router-link
                  >
                </li>
              </template>
              <li class="breadcrumb-item" v-if="route.name === 'TableScreen' && nameTableScreen !== summaryName">
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameTableScreen
                }}</router-link>
              </li>
              <li class="breadcrumb-item" v-if="route.name === 'DichVu' && nameService !== summaryName">
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameService
                }}</router-link>
              </li>
            </template>

            <!-- Other routes breadcrumb (probe) -->
            <template v-else>
              <template v-for="(item, index) in breadcrumbItems" :key="index">
                <li class="breadcrumb-item" :class="{ active: item[0] }">
                  <router-link
                    :to="{ path: `${item.href}` }"
                    class="breadcrumb-link"
                    @click="handleBreadcrumbClick($event, item, index)"
                    >{{ item.text }}</router-link
                  >
                </li>
              </template>
              <li class="breadcrumb-item" v-if="route.name === 'TableScreen'">
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameTableScreen
                }}</router-link>
              </li>
              <li class="breadcrumb-item" v-if="route.name === 'DichVu'">
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameService
                }}</router-link>
              </li>
              <li
                class="breadcrumb-item"
                v-if="route.name === 'Dynamic' && nameMenuParent"
              >
                <router-link
                  :to="{ path: `` }"
                  :class="
                    nameMenuChild ? 'breadcrumb-link' : 'breadcrumb-link-page'
                  "
                  >{{ nameMenuParent }}</router-link
                >
              </li>
              <li
                class="breadcrumb-item"
                v-if="route.name === 'Dynamic' && nameMenuChild"
              >
                <router-link :to="{ path: `` }" class="breadcrumb-link-page">{{
                  nameMenuChild
                }}</router-link>
              </li>
              <li class="breadcrumb-item" v-if="route.name === 'Usecases621'">
                <router-link :to="{ path: `` }"
                  >Usecases 6.2.1 {{ nameUsecases }}</router-link
                >
              </li>
            </template>
          </ol>
        </div>
      </div>
    </div>
  </div>
  <!-- end page title -->
</template>
<style scoped lang="scss">
.breadcrumb-link,
.breadcrumb-link-page {
  font-weight: 400;
  font-size: var(--cem-font-size);
  line-height: var(--cem-line-height);
  background-color: #fff;
  border-radius: 8px;
  padding: 4px 8px 4px 8px;
  &:hover {
    background-color: #f2f2f2;
  }
}
.breadcrumb-item {
  cursor: pointer;
}
.breadcrumb-link {
  color: #a4a4a4;
}
.breadcrumb-link-page {
  color: #1c1c1c;
}
</style>
